import { JsonArray } from './jsonArray.js';
import { JsonObject } from './jsonObject.js';
export { JsonArray } from './jsonArray.js';
export { JsonObject } from './jsonObject.js';

export const JsonValueType = {
    String: 'String',
    Number: 'Number',
    Integer: 'Integer',
    Bool: 'Bool',
    Array: 'Array',
    Object: 'Object',
    Null: 'Null'
} as const;
export type JsonValueType = typeof JsonValueType[keyof typeof JsonValueType];

type JsonValueInner = string | number | boolean | JsonArray | JsonObject | null;

interface MatchHandlers<T> {
    onString?: (value: string) => T;
    onNumber?: (value: number) => T;
    onInteger?: (value: number) => T;
    onBool?: (value: boolean) => T;
    onArray?: (value: JsonArray) => T;
    onObject?: (value: JsonObject) => T;
    onNull?: () => T;
}

export class JsonValue {
    readonly _type: JsonValueType;
    readonly _value: JsonValueInner;

    constructor(type: JsonValueType, value: JsonValueInner) {
        this._type = type;
        this._value = value;
    }

    static jsonValue(type: JsonValueType, value: JsonValueInner): JsonValue {
        return new JsonValue(type, value);
    }

    static string(value: string): JsonValue {
        return new JsonValue(JsonValueType.String, value);
    }

    static number(value: number): JsonValue {
        return new JsonValue(JsonValueType.Number, value);
    }

    static integer(value: number): JsonValue {
        return new JsonValue(JsonValueType.Integer, value);
    }

    static bool(value: boolean): JsonValue {
        return new JsonValue(JsonValueType.Bool, value);
    }

    static array(value: JsonArray): JsonValue {
        return new JsonValue(JsonValueType.Array, value);
    }

    static object(value: JsonObject): JsonValue {
        return new JsonValue(JsonValueType.Object, value);
    }

    static null(): JsonValue {
        return new JsonValue(JsonValueType.Null, null);
    }

    static from(value: unknown): JsonValue {
        if (value instanceof JsonValue) {
            return value;
        }
        if (value === null || value === undefined) {
            return JsonValue.null();
        }
        if (typeof value === 'string') {
            return JsonValue.string(value);
        }
        if (typeof value === 'boolean') {
            return JsonValue.bool(value);
        }
        if (typeof value === 'number') {
            if (Number.isInteger(value)) {
                return JsonValue.integer(value);
            }
            return JsonValue.number(value);
        }
        if (value && (value as object).constructor && (value as object).constructor.name === 'JsonArray') {
            return JsonValue.array(value as JsonArray);
        }
        if (value && (value as object).constructor && (value as object).constructor.name === 'JsonObject') {
            return JsonValue.object(value as JsonObject);
        }
        if (Array.isArray(value)) {
            return JsonValue.array(value as unknown as JsonArray);
        }
        if (typeof value === 'object') {
            return JsonValue.object(value as unknown as JsonObject);
        }
        return JsonValue.string(String(value));
    }

    get typeName(): JsonValueType {
        return this._type;
    }

    get isString(): boolean {
        return this._type === JsonValueType.String;
    }

    get isNumber(): boolean {
        return this._type === JsonValueType.Number;
    }

    get isInteger(): boolean {
        return this._type === JsonValueType.Integer;
    }

    get isBool(): boolean {
        return this._type === JsonValueType.Bool;
    }

    get isArray(): boolean {
        return this._type === JsonValueType.Array;
    }

    get isObject(): boolean {
        return this._type === JsonValueType.Object;
    }

    get isNull(): boolean {
        return this._type === JsonValueType.Null;
    }

    asString(): string | null {
        return this._type === JsonValueType.String ? this._value as string : null;
    }

    asNumber(): number | null {
        return this._type === JsonValueType.Number ? this._value as number : null;
    }

    asInteger(): number | null {
        return this._type === JsonValueType.Integer ? this._value as number : null;
    }

    asBool(): boolean | null {
        return this._type === JsonValueType.Bool ? this._value as boolean : null;
    }

    asArray(): JsonArray | null {
        return this._type === JsonValueType.Array ? this._value as JsonArray : null;
    }

    asObject(): JsonObject | null {
        return this._type === JsonValueType.Object ? this._value as JsonObject : null;
    }

    getString(): string {
        if (this._type !== JsonValueType.String) {
            throw new Error(`JsonValue is ${this._type}, not String`);
        }
        return this._value as string;
    }

    getNumber(): number {
        if (this._type !== JsonValueType.Number) {
            throw new Error(`JsonValue is ${this._type}, not Number`);
        }
        return this._value as number;
    }

    getInteger(): number {
        if (this._type !== JsonValueType.Integer) {
            throw new Error(`JsonValue is ${this._type}, not Integer`);
        }
        return this._value as number;
    }

    getBool(): boolean {
        if (this._type !== JsonValueType.Bool) {
            throw new Error(`JsonValue is ${this._type}, not Bool`);
        }
        return this._value as boolean;
    }

    getArray(): JsonArray {
        if (this._type !== JsonValueType.Array) {
            throw new Error(`JsonValue is ${this._type}, not Array`);
        }
        return this._value as JsonArray;
    }

    getObject(): JsonObject {
        if (this._type !== JsonValueType.Object) {
            throw new Error(`JsonValue is ${this._type}, not Object`);
        }
        return this._value as JsonObject;
    }

    match<T>(handlers: MatchHandlers<T>): T | undefined {
        const { onString, onNumber, onInteger, onBool, onArray, onObject, onNull } = handlers;
        switch (this._type) {
            case JsonValueType.String:
                return onString ? onString(this._value as string) : undefined;
            case JsonValueType.Number:
                return onNumber ? onNumber(this._value as number) : undefined;
            case JsonValueType.Integer:
                return onInteger ? onInteger(this._value as number) : undefined;
            case JsonValueType.Bool:
                return onBool ? onBool(this._value as boolean) : undefined;
            case JsonValueType.Array:
                return onArray ? onArray(this._value as JsonArray) : undefined;
            case JsonValueType.Object:
                return onObject ? onObject(this._value as JsonObject) : undefined;
            case JsonValueType.Null:
                return onNull ? onNull() : undefined;
            default:
                throw new Error(`Unknown JsonValue type: ${this._type}`);
        }
    }

    equals(other: JsonValue): boolean {
        if (!(other instanceof JsonValue)) return false;
        if (this._type !== other._type) return false;
        if (this._type === JsonValueType.Null) return true;
        return this._value === other._value;
    }

    /**
     * Returns a structural deep copy of this value. Scalars and null are value-immutable and
     * returned as-is; arrays and objects are recursively cloned into fresh mutable instances.
     * Replaces the historical serialize-then-parse clone round-trip.
     */
    deepClone(): JsonValue {
        if (this._type === JsonValueType.Array) {
            return JsonValue.array((this._value as JsonArray).deepClone());
        }
        if (this._type === JsonValueType.Object) {
            return JsonValue.object((this._value as JsonObject).deepClone());
        }
        return this;
    }

    getHashCode(): number {
        const text = this.toString();
        let hash = 17;
        for (let i = 0; i < text.length; i++) {
            hash = ((hash * 31) + text.charCodeAt(i)) | 0;
        }
        return hash;
    }

    toBool(): boolean {
        const boolValue = this.asBool();
        if (boolValue !== null) {
            return boolValue;
        }

        const stringValue = this.asString();
        if (stringValue !== null) {
            return stringValue.toLowerCase() === 'true';
        }

        const numberValue = this.asNumber() ?? this.asInteger();
        if (numberValue !== null) {
            return numberValue !== 0;
        }

        return false;
    }

    toStringOrEmpty(): string {
        return this.isNull ? '' : this.toString();
    }

    toString(): string {
        switch (this._type) {
            case JsonValueType.String:
                return this._value as string;
            case JsonValueType.Number:
                return String(this._value);
            case JsonValueType.Integer:
                return String(this._value);
            case JsonValueType.Bool:
                return String(this._value).toLowerCase();
            case JsonValueType.Array:
                return '[Array]';
            case JsonValueType.Object:
                return '[Object]';
            case JsonValueType.Null:
                return 'null';
            default:
                return `[Unknown:${this._type}]`;
        }
    }
}

export function toJsonValue(value: unknown): JsonValue {
    if (value instanceof JsonValue) {
        return value;
    }

    if (value === null || value === undefined) {
        return JsonValue.null();
    }

    if (typeof value === 'string') {
        return JsonValue.string(value);
    }

    if (typeof value === 'boolean') {
        return JsonValue.bool(value);
    }

    if (typeof value === 'number') {
        if (Number.isInteger(value)) {
            return JsonValue.integer(value);
        }
        return JsonValue.number(value);
    }

    if (value && (value as object).constructor && (value as object).constructor.name === 'JsonArray') {
        return JsonValue.array(value as JsonArray);
    }

    if (value instanceof JsonObject) {
        return JsonValue.object(value);
    }

    if (Array.isArray(value)) {
        const jsonArray = new JsonArray();
        for (const item of value) {
            jsonArray.add(item);
        }
        return JsonValue.array(jsonArray);
    }

    if (typeof value === 'object') {
        const jsonObject = new JsonObject();
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
            jsonObject.set(k, v);
        }
        return JsonValue.object(jsonObject);
    }

    return JsonValue.string(String(value));
}
